import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import WhiskeyLogo from './layout/WhiskeyLogo';
import AuthService from '../services/AuthService';

type Step = 'request' | 'reset' | 'done';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  fontSize: 14,
  outline: 'none',
  transition: 'border-color 140ms ease-out, box-shadow 140ms ease-out',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
};

function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
  e.target.style.borderColor = 'var(--ember)';
  e.target.style.boxShadow = '0 0 0 3px var(--ember-soft)';
}
function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
  e.target.style.borderColor = 'var(--border)';
  e.target.style.boxShadow = 'none';
}

const primaryButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  borderRadius: 10,
  border: 'none',
  background: 'var(--ink)',
  color: 'white',
  fontWeight: 700,
  fontSize: 14.5,
  cursor: 'pointer',
};

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await AuthService.forgotPassword(email);
      // Backend deliberately doesn't reveal whether the email exists --
      // always advance to the reset step with the same generic message.
      setStep('reset');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await AuthService.resetPassword(email, code, newPassword);
      setStep('done');
    } catch (err: any) {
      setError(err.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ws-shell" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', background: 'var(--paper)' }}>
      <div className="ws-fade-up" style={{ width: '100%', maxWidth: 400, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 36, boxShadow: '0 1px 2px oklch(0.2 0.02 260 / 0.06), 0 12px 32px oklch(0.2 0.02 260 / 0.05)' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 28 }}>
          <WhiskeyLogo size={26} />
          <span className="ws-display" style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>Whiskey Sensei</span>
        </Link>

        {step === 'request' && (
          <>
            <h1 className="ws-display" style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Reset your password</h1>
            <p style={{ margin: '0 0 24px', fontSize: 13.5, color: 'var(--text-tertiary)' }}>
              Enter the email on your account and we'll send you a reset code.
            </p>
            {error && (
              <div style={{ marginBottom: 18, padding: '10px 14px', borderRadius: 8, background: 'oklch(0.6 0.2 25 / 0.1)', border: '1px solid oklch(0.6 0.2 25 / 0.35)', color: 'var(--danger)', fontSize: 13 }}>
                {error}
              </div>
            )}
            <form onSubmit={handleRequest} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle} htmlFor="fp-email">Email</label>
                <input id="fp-email" type="email" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jordan@company.com" />
              </div>
              <button type="submit" disabled={loading} className="btn-live" style={{ ...primaryButtonStyle, opacity: loading ? 0.7 : 1, cursor: loading ? 'default' : 'pointer' }}>
                {loading ? 'Sending…' : 'Send reset code'}
              </button>
            </form>
          </>
        )}

        {step === 'reset' && (
          <>
            <h1 className="ws-display" style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Check your email</h1>
            <p style={{ margin: '0 0 24px', fontSize: 13.5, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
              If an account exists for <strong style={{ color: 'var(--text-secondary)' }}>{email}</strong>, a reset code is on its way. Enter it below along with your new password.
            </p>
            {error && (
              <div style={{ marginBottom: 18, padding: '10px 14px', borderRadius: 8, background: 'oklch(0.6 0.2 25 / 0.1)', border: '1px solid oklch(0.6 0.2 25 / 0.35)', color: 'var(--danger)', fontSize: 13 }}>
                {error}
              </div>
            )}
            <form onSubmit={handleReset} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle} htmlFor="fp-code">Reset code</label>
                <input id="fp-code" type="text" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                  value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" />
              </div>
              <div>
                <label style={labelStyle} htmlFor="fp-new-password">New password</label>
                <input id="fp-new-password" type="password" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                <p style={{ margin: '6px 0 0', fontSize: 11.5, color: 'var(--text-tertiary)' }}>8+ characters, uppercase, lowercase, number, symbol</p>
              </div>
              <div>
                <label style={labelStyle} htmlFor="fp-confirm-password">Confirm new password</label>
                <input id="fp-confirm-password" type="password" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
              <button type="submit" disabled={loading} className="btn-live" style={{ ...primaryButtonStyle, opacity: loading ? 0.7 : 1, cursor: loading ? 'default' : 'pointer' }}>
                {loading ? 'Setting password…' : 'Set new password'}
              </button>
            </form>
            <button type="button" onClick={() => { setStep('request'); setError(''); }} style={{ marginTop: 16, width: '100%', textAlign: 'center', background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', cursor: 'pointer' }}>
              Use a different email
            </button>
          </>
        )}

        {step === 'done' && (
          <>
            <h1 className="ws-display" style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Password reset</h1>
            <p style={{ margin: '0 0 24px', fontSize: 13.5, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
              Ruff! Your password's been updated. Log in with your new password to pick up where you left off.
            </p>
            <button type="button" onClick={() => navigate('/login')} className="btn-live" style={primaryButtonStyle}>
              Continue to log in
            </button>
          </>
        )}

        {step !== 'done' && (
          <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13, color: 'var(--text-tertiary)' }}>
            Remembered your password?{' '}
            <Link to="/login" style={{ fontWeight: 700, color: 'var(--ember-strong)', textDecoration: 'none' }}>Log in</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
